'use client';

import React, { useRef, useEffect, useState } from 'react';
import { Brain, Zap, Target, ShieldCheck, AlertTriangle, GitBranch, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useSession } from './SessionProvider';
import CLIRadialCard from './CLIRadialCard';


interface PrevMetrics {
  attentionScore: number;
  signalQualityIndex: number;
  artifactRate: number;
  thetaAlphaRatio: number;
  interHemisphericCoherence: number;
}

function TrendIcon({ current, prev, inverse = false }: { current: number; prev: number; inverse?: boolean }) {
  const diff = current - prev;
  const threshold = 0.5;
  if (Math.abs(diff) < threshold) return <Minus size={12} className="text-zinc-500" />;
  const isPositive = inverse ? diff < 0 : diff > 0;
  if (isPositive) return <TrendingUp size={12} className="text-emerald-400" />;
  return <TrendingDown size={12} className="text-red-400" />;
}

// Animated counter hook — smooth eased transition to new value
function useAnimatedValue(target: number, duration = 500) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const fromRef = useRef(target);

  useEffect(() => {
    const from = fromRef.current;
    const to = target;
    if (Math.abs(to - from) < 0.05) return;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startRef.current = null;

    const animate = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (to - from) * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return display;
}

function SmallMetricCard({
  label,
  value,
  unit,
  icon: IconComp,
  iconColor,
  trend,
  prevValue,
  inverse = false,
  highlight,
  sublabel,
  pulseOrange = false,
}: {
  label: string;
  value: number;
  unit: string;
  icon: React.ElementType;
  iconColor: string;
  trend?: number;
  prevValue?: number;
  inverse?: boolean;
  highlight?: 'good' | 'warn' | 'danger' | 'neutral';
  sublabel?: string;
  pulseOrange?: boolean;
}) {
  const [flash, setFlash] = useState(false);
  const prevValueRef = useRef(value);
  const animatedValue = useAnimatedValue(value);

  // Trigger flash highlight on data change
  useEffect(() => {
    if (Math.abs(value - prevValueRef.current) > 0.5) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 650);
      prevValueRef.current = value;
      return () => clearTimeout(t);
    }
    prevValueRef.current = value;
  }, [value]);

  const borderColor = {
    good: 'border-emerald-800/40 hover:border-emerald-600/50',
    warn: 'border-amber-800/40 hover:border-[#F97316]/50',
    danger: 'border-red-800/40 hover:border-red-600/50',
    neutral: 'border-[#1E3A5F]/50 hover:border-[#F97316]/30',
  }[highlight ?? 'neutral'];

  const bgColor = {
    good: 'bg-emerald-950/20',
    warn: 'bg-amber-950/15',
    danger: 'bg-red-950/20',
    neutral: 'bg-[#0B1628]/70',
  }[highlight ?? 'neutral'];

  const valueColor = {
    good: 'text-emerald-300',
    warn: 'text-amber-300',
    danger: 'text-red-300',
    neutral: 'text-zinc-100',
  }[highlight ?? 'neutral'];

  const glowColor = {
    good: 'rgba(52,211,153,0.05)',
    warn: 'rgba(249,115,22,0.05)',
    danger: 'rgba(248,113,113,0.05)',
    neutral: 'rgba(45,90,142,0.05)',
  }[highlight ?? 'neutral'];

  // Orange pulse glow for attention + beta cards
  const orangeGlowStyle = pulseOrange
    ? {
        boxShadow: flash
          ? '0 0 24px 6px rgba(249,115,22,0.5), 0 0 0 1px rgba(249,115,22,0.35)'
          : '0 0 16px rgba(249,115,22,0.18)',
        transition: 'box-shadow 0.35s ease',
      }
    : { boxShadow: '0 2px 12px rgba(0,0,0,0.2)' };

  const displayValue =
    unit === 'µV²'
      ? animatedValue.toFixed(1)
      : unit === '/min' || unit === 'ratio'
      ? animatedValue.toFixed(2)
      : Math.round(animatedValue).toString();

  return (
    <div
      className={`rounded-xl border p-4 md:p-5 flex flex-col gap-3 transition-all duration-300 cursor-default group relative overflow-hidden ${borderColor} ${bgColor}`}
      style={orangeGlowStyle}
    >
      {/* Hover glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 100%, ${glowColor} 0%, transparent 70%)` }}
      />

      {/* Pulsing orange bottom glow for attention/beta cards */}
      {pulseOrange && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 120%, rgba(249,115,22,0.12) 0%, transparent 60%)',
            animation: 'metricPulse 3s ease-in-out infinite',
          }}
        />
      )}

      {/* Data-triggered flash overlay */}
      {flash && (
        <div
          className="absolute inset-0 pointer-events-none rounded-xl"
          style={{
            background: 'radial-gradient(ellipse at 50% 50%, rgba(249,115,22,0.15) 0%, transparent 70%)',
            animation: 'metricFlash 0.65s ease-out forwards',
          }}
        />
      )}

      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0B1628]/80 border border-[#1E3A5F]/50 group-hover:border-[#F97316]/30 transition-colors flex-shrink-0"
            style={pulseOrange ? { animation: 'iconGlow 3s ease-in-out infinite' } : undefined}
          >
            <IconComp size={15} className={iconColor} />
          </div>
          <span className="text-[12px] md:text-[13px] font-bold text-[#F97316] tracking-wide leading-tight">{label}</span>
        </div>
        {prevValue !== undefined && (
          <TrendIcon current={value} prev={prevValue} inverse={inverse} />
        )}
      </div>

      <div className="relative z-10">
        <div className="flex items-end gap-1.5">
          <span
            className={`text-2xl md:text-3xl font-bold font-mono tabular-nums ${valueColor}`}
            style={{ transition: 'color 0.3s ease' }}
          >
            {displayValue}
          </span>
          <span className="text-[12px] md:text-[13px] text-zinc-500 mb-1">{unit}</span>
        </div>
        {sublabel && <p className="text-[11px] md:text-[12px] text-[#2D5A8E] mt-0.5">{sublabel}</p>}
      </div>

      <style jsx>{`
        @keyframes metricPulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        @keyframes metricFlash {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes iconGlow {
          0%, 100% { box-shadow: 0 0 6px rgba(249,115,22,0.2); }
          50% { box-shadow: 0 0 14px rgba(249,115,22,0.55); }
        }
      `}</style>
    </div>
  );
}

export default function MetricsBentoGrid() {
  const { metrics, status } = useSession();
  const prevRef = useRef<PrevMetrics>({
    attentionScore: metrics.attentionScore,
    signalQualityIndex: metrics.signalQualityIndex,
    artifactRate: metrics.artifactRate,
    thetaAlphaRatio: metrics.thetaAlphaRatio,
    interHemisphericCoherence: metrics.interHemisphericCoherence,
  });
  const [prev, setPrev] = useState<PrevMetrics>(prevRef.current);

  useEffect(() => {
    const snapshot = { ...prevRef.current };
    setPrev(snapshot);
    prevRef.current = {
      attentionScore: metrics.attentionScore,
      signalQualityIndex: metrics.signalQualityIndex,
      artifactRate: metrics.artifactRate,
      thetaAlphaRatio: metrics.thetaAlphaRatio,
      interHemisphericCoherence: metrics.interHemisphericCoherence,
    };
  }, [metrics]);

  const sqiHighlight = metrics.signalQualityIndex >= 85 ? 'good' : metrics.signalQualityIndex >= 65 ? 'warn' : 'danger';
  const artifactHighlight = metrics.artifactRate < 1.5 ? 'good' : metrics.artifactRate < 3 ? 'warn' : 'danger';
  const cohHighlight = metrics.interHemisphericCoherence >= 70 ? 'good' : metrics.interHemisphericCoherence >= 50 ? 'warn' : 'danger';

  if (status === 'connecting') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 animate-pulse">
        <div className="sm:col-span-2 h-40 sm:h-44 rounded-xl bg-[#0B1628]/60 border border-[#1E3A5F]/40" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`skel-metric-${i}`} className="h-36 sm:h-44 rounded-xl bg-[#0B1628]/60 border border-[#1E3A5F]/40" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
      {/* Hero: Cognitive Load Index — full width on all sizes */}
      <div className="col-span-2">
        <CLIRadialCard />
      </div>

      <SmallMetricCard
        label="Attention Score"
        value={metrics.attentionScore}
        unit="/100"
        icon={Target}
        iconColor="text-sky-400"
        prevValue={prev.attentionScore}
        highlight={metrics.attentionScore >= 70 ? 'good' : metrics.attentionScore >= 45 ? 'warn' : 'danger'}
        sublabel={metrics.attentionScore >= 70 ? 'Sustained focus' : metrics.attentionScore >= 45 ? 'Moderate drift' : 'Attention lapse'}
        pulseOrange
      />

      <SmallMetricCard
        label="Signal Quality"
        value={metrics.signalQualityIndex}
        unit="SQI"
        icon={ShieldCheck}
        iconColor={sqiHighlight === 'good' ? 'text-emerald-400' : sqiHighlight === 'warn' ? 'text-amber-400' : 'text-red-400'}
        prevValue={prev.signalQualityIndex}
        highlight={sqiHighlight}
        sublabel={sqiHighlight === 'good' ? 'Clean signal' : sqiHighlight === 'warn' ? 'Minor noise' : 'High noise floor'}
      />

      <SmallMetricCard
        label="Theta/Alpha Ratio"
        value={metrics.thetaAlphaRatio}
        unit="ratio"
        icon={Brain}
        iconColor="text-violet-400"
        prevValue={prev.thetaAlphaRatio}
        highlight={metrics.thetaAlphaRatio > 2.0 ? 'danger' : metrics.thetaAlphaRatio > 1.4 ? 'warn' : 'good'}
        sublabel={metrics.thetaAlphaRatio > 2.0 ? 'High workload' : metrics.thetaAlphaRatio > 1.4 ? 'Moderate load' : 'Relaxed state'}
      />

      <SmallMetricCard
        label="Artifact Rate"
        value={metrics.artifactRate}
        unit="/min"
        icon={AlertTriangle}
        iconColor={artifactHighlight === 'good' ? 'text-emerald-400' : artifactHighlight === 'warn' ? 'text-amber-400' : 'text-red-400'}
        prevValue={prev.artifactRate}
        inverse
        highlight={artifactHighlight}
        sublabel={artifactHighlight === 'good' ? 'Clean recording' : artifactHighlight === 'warn' ? 'Moderate artifacts' : 'High contamination'}
      />

      <SmallMetricCard
        label="IH Coherence"
        value={metrics.interHemisphericCoherence}
        unit="%"
        icon={GitBranch}
        iconColor="text-fuchsia-400"
        prevValue={prev.interHemisphericCoherence}
        highlight={cohHighlight}
        sublabel="Left/right synchrony"
      />

      <SmallMetricCard
        label="Beta Power"
        value={metrics.betaPower}
        unit="µV²"
        icon={Zap}
        iconColor="text-[#F97316]"
        highlight={metrics.betaPower > 28 ? 'danger' : metrics.betaPower > 18 ? 'warn' : 'neutral'}
        sublabel="13–30 Hz active thinking"
        pulseOrange
      />
    </div>
  );
}